<template>
  <div class="automation-page flex-1 min-h-0 flex flex-col p-4">
    <woot-button
      color-scheme="success"
      class-names="button--fixed-top"
      icon="add-circle"
      @click="openAddPopup()"
    >
      {{ $t('AUTOMATION.HEADER_BTN_TXT') }}
    </woot-button>

    <div
      class="automation-page__body flex-1 min-h-0 flex flex-col"
      :class="{ 'automation-page__body--with-footer': showTableFooter }"
    >
      <div class="automation-header">
        <div class="automation-search-wrap">
          <div class="search-icon-wrap" @click="applySearch">
            <fluent-icon icon="search" class="search-icon" />
          </div>
          <input
            type="text"
            :placeholder="$t('AUTOMATION.SEARCH_PLACEHOLDER')"
            class="automation-search-input"
            :value="searchQuery"
            @keyup.enter="applySearch"
            @input="onSearchInput"
          />
        </div>
      </div>

      <div class="flex flex-row gap-4 flex-1 min-h-0 overflow-hidden">
        <div class="w-[60%] automation-list-column flex-1 min-h-0 overflow-auto">
          <woot-loading-state
            v-if="uiFlags.isFetching || !hasLoadedOnce"
            :message="$t('AUTOMATION.LOADING')"
          />
          <p
            v-else-if="!filteredRecords.length"
            class="automation-empty-state"
          >
            {{ emptyStateMessage }}
          </p>

          <table v-else class="woot-table">
            <thead>
              <th
                v-for="thHeader in $t('AUTOMATION.LIST.TABLE_HEADER')"
                :key="thHeader"
              >
                {{ thHeader }}
              </th>
            </thead>
            <tbody>
              <tr v-for="automation in paginatedRecords" :key="automation.id">
                <td>{{ automation.name }}</td>
                <td>
                  <div
                    v-tooltip="automation.description"
                    class="truncate max-w-xs"
                  >
                    {{ automation.description }}
                  </div>
                </td>
                <td>
                  <woot-switch
                    :value="automation.active"
                    @input="toggleAutomation(automation, automation.active)"
                  />
                </td>
                <td>{{ readableTime(automation.created_on) }}</td>
                <td class="button-wrapper">
                  <woot-button
                    v-tooltip.top="$t('AUTOMATION.FORM.EDIT')"
                    variant="smooth"
                    size="tiny"
                    color-scheme="secondary"
                    class-names="grey-btn"
                    :is-loading="loading[automation.id]"
                    icon="edit"
                    @click="openEditPopup(automation)"
                  />
                  <woot-button
                    v-tooltip.top="$t('AUTOMATION.CLONE.TOOLTIP')"
                    variant="smooth"
                    size="tiny"
                    color-scheme="primary"
                    class-names="grey-btn"
                    :is-loading="loading[automation.id]"
                    icon="copy"
                    @click="cloneAutomation(automation.id)"
                  />
                  <woot-button
                    v-tooltip.top="$t('AUTOMATION.FORM.DELETE')"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    icon="dismiss-circle"
                    class-names="grey-btn"
                    :is-loading="loading[automation.id]"
                    @click="openDeletePopup(automation)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="w-[34%] automation-sidebar overflow-auto">
          <span v-dompurify-html="$t('AUTOMATION.SIDEBAR_TXT')" />
        </div>
      </div>
    </div>

    <div
      v-if="showTableFooter"
      class="table-footer-wrap table-footer-sticky"
    >
      <table-footer
        :current-page="currentPage"
        :page-size="perPage"
        :total-count="filteredRecords.length"
        @page-change="onPageChange"
      />
    </div>

    <woot-modal
      :show.sync="showAddPopup"
      size="medium"
      content-class="automation-rule-modal"
      :on-close="hideAddPopup"
    >
      <add-automation-rule
        v-if="showAddPopup"
        :on-close="hideAddPopup"
        @saveAutomation="submitAutomation"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('AUTOMATION.DELETE.CONFIRM.YES')"
      :reject-text="$t('AUTOMATION.DELETE.CONFIRM.NO')"
    />

    <woot-modal
      :show.sync="showEditPopup"
      size="medium"
      content-class="automation-rule-modal"
      :on-close="hideEditPopup"
    >
      <edit-automation-rule
        v-if="showEditPopup"
        :on-close="hideEditPopup"
        :selected-response="selectedResponse"
        @saveAutomation="submitAutomation"
      />
    </woot-modal>
    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import AddAutomationRule from './AddAutomationRule.vue';
import EditAutomationRule from './EditAutomationRule.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import alertMixin from 'shared/mixins/alertMixin';
import timeMixin from 'dashboard/mixins/time';

export default {
  components: {
    AddAutomationRule,
    EditAutomationRule,
    TableFooter,
  },
  mixins: [alertMixin, timeMixin],
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedResponse: {},
      hasLoadedOnce: false,
      searchQuery: '',
      currentPage: 1,
      perPage: 20,
      toggleModalTitle: this.$t('AUTOMATION.TOGGLE.ACTIVATION_TITLE'),
      toggleModalDescription: this.$t(
        'AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION'
      ),
    };
  },
  computed: {
    ...mapGetters({
      records: ['automations/getAutomations'],
      uiFlags: 'automations/getUIFlags',
    }),
    filteredRecords() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        return this.records;
      }

      return this.records.filter(automation =>
        this.matchesSearch(automation, query)
      );
    },
    paginatedRecords() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredRecords.slice(start, start + this.perPage);
    },
    emptyStateMessage() {
      if (this.searchQuery.trim()) {
        return this.$t('AUTOMATION.SEARCH_404');
      }
      return this.$t('AUTOMATION.LIST.404');
    },
    showTableFooter() {
      return (
        !this.uiFlags.isFetching &&
        this.hasLoadedOnce &&
        this.filteredRecords.length > 0
      );
    },
    deleteMessage() {
      return ` ${this.selectedResponse.name}?`;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('agents/get');
    this.$store.dispatch('contacts/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('labels/get');
    this.$store.dispatch('campaigns/get');
    this.$store.dispatch('boards/get');
    this.$store.dispatch('automations/get').finally(() => {
      this.hasLoadedOnce = true;
    });
  },
  methods: {
    onSearchInput(event) {
      this.searchQuery = event.target.value;
    },
    applySearch() {
      this.currentPage = 1;
    },
    onPageChange(page) {
      this.currentPage = page;
    },
    adjustPageAfterDataChange() {
      const maxPage = Math.max(
        1,
        Math.ceil(this.filteredRecords.length / this.perPage)
      );
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    },
    matchesSearch(automation, query) {
      const activeLabel = automation.active ? 'active' : 'inactive';
      const createdOn = automation.created_on
        ? this.readableTime(automation.created_on).toLowerCase()
        : '';

      return (
        String(automation.id || '').includes(query) ||
        (automation.name && automation.name.toLowerCase().includes(query)) ||
        (automation.description &&
          automation.description.toLowerCase().includes(query)) ||
        activeLabel.includes(query) ||
        (automation.created_on &&
          String(automation.created_on).toLowerCase().includes(query)) ||
        createdOn.includes(query)
      );
    },
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openEditPopup(response) {
      this.selectedResponse = response;
      this.showEditPopup = true;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedResponse.id] = true;
      this.closeDeletePopup();
      this.deleteAutomation(this.selectedResponse.id);
    },
    async deleteAutomation(id) {
      try {
        await this.$store.dispatch('automations/delete', id);
        this.showAlert(this.$t('AUTOMATION.DELETE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(this.$t('AUTOMATION.DELETE.API.ERROR_MESSAGE'));
      } finally {
        this.loading[id] = false;
        this.adjustPageAfterDataChange();
      }
    },
    async cloneAutomation(id) {
      try {
        await this.$store.dispatch('automations/clone', id);
        this.showAlert(this.$t('AUTOMATION.CLONE.API.SUCCESS_MESSAGE'));
        await this.$store.dispatch('automations/get');
      } catch (error) {
        this.showAlert(this.$t('AUTOMATION.CLONE.API.ERROR_MESSAGE'));
      }
    },
    async submitAutomation(payload, mode) {
      try {
        const action =
          mode === 'edit' ? 'automations/update' : 'automations/create';
        const successMessage =
          mode === 'edit'
            ? this.$t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE')
            : this.$t('AUTOMATION.ADD.API.SUCCESS_MESSAGE');
        await this.$store.dispatch(action, payload);
        this.showAlert(successMessage);
        this.hideAddPopup();
        this.hideEditPopup();
      } catch (error) {
        const errorMessage =
          mode === 'edit'
            ? this.$t('AUTOMATION.EDIT.API.ERROR_MESSAGE')
            : this.$t('AUTOMATION.ADD.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
    async toggleAutomation(automation, status) {
      try {
        this.toggleModalTitle = status
          ? this.$t('AUTOMATION.TOGGLE.DEACTIVATION_TITLE')
          : this.$t('AUTOMATION.TOGGLE.ACTIVATION_TITLE');
        this.toggleModalDescription = status
          ? this.$t('AUTOMATION.TOGGLE.DEACTIVATION_DESCRIPTION', {
              automationName: automation.name,
            })
          : this.$t('AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION', {
              automationName: automation.name,
            });
        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          await this.$store.dispatch('automations/update', {
            id: automation.id,
            active: !status,
          });
          const message = status
            ? this.$t('AUTOMATION.TOGGLE.DEACTIVATION_SUCCESFUL')
            : this.$t('AUTOMATION.TOGGLE.ACTIVATION_SUCCESFUL');
          this.showAlert(message);
        }
      } catch (error) {
        this.showAlert(this.$t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
      }
    },
    readableTime(date) {
      return this.messageStamp(new Date(date), 'LLL d, h:mm a');
    },
  },
};
</script>

<style scoped lang="scss">
@import 'dashboard/assets/scss/variables';

.automation-page {
  overflow: hidden;
}

.automation-page__body {
  min-height: 0;

  &--with-footer {
    padding-bottom: 50px;
  }
}

.automation-list-column {
  min-height: 0;
}

.automation-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #64748b;

  @apply dark:text-slate-400;
}

.table-footer-wrap {
  flex-shrink: 0;
  min-width: 0;
}

.table-footer-sticky {
  position: fixed;
  right: 0;
  bottom: 0;
  width: calc(100vw - 3.5rem);
  z-index: 3;
  background: #f9f9f9;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.05);

  @apply dark:bg-slate-800 dark:border-slate-700;
}

.automation-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.automation-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 320px;
  max-width: 100%;
  flex-shrink: 1;

  .search-icon-wrap {
    position: absolute;
    right: 12px;
    display: flex;
    align-items: center;
    height: 100%;
    cursor: pointer;
    z-index: 1;
    color: #94a3b8;

    @apply dark:text-slate-400;

    :deep(svg) {
      height: 18px;
      width: 18px;
    }
  }

  .automation-search-input {
    width: 100%;
    height: 38px;
    padding: 0 40px 0 12px;
    font-size: 13px;
    border: 1px solid #ccc;
    border-radius: 6px;
    background: white;
    margin: 0;
    box-sizing: border-box;

    @apply dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200;

    &::placeholder {
      color: #9ca3af;
      @apply dark:text-slate-500;
    }

    &:focus {
      outline: none;
      border-color: #1f93ff;
      box-shadow: 0 0 0 1px #1f93ff;
    }
  }
}
</style>
